import { useEffect, useRef, useState } from 'react';
import { IndustryApplication } from '../types';

interface IndustrySectionProps {
  industries: IndustryApplication[];
  className?: string;
}

const IndustrySection = ({ industries, className = '' }: IndustrySectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<string>(industries[0]?.id || '');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const selectedIndustryData = industries.find((ind) => ind.id === selectedIndustry);

  return (
    <section
      id="industries"
      ref={sectionRef}
      className={`relative py-20 px-6 ${className}`}
    >
      <div className="container mx-auto max-w-6xl">
        <div
          className={`text-center space-y-4 mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient">
            Industry Applications
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Proven expertise across mission-critical sectors
          </p>
        </div>

        <div
          className={`flex flex-wrap justify-center gap-3 mb-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          {industries.map((industry) => (
            <button
              key={industry.id}
              onClick={() => setSelectedIndustry(industry.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-250 ${
                selectedIndustry === industry.id
                  ? 'bg-primary text-primary-foreground shadow-glass'
                  : 'glass-effect text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {industry.name}
            </button>
          ))}
        </div>

        {selectedIndustryData && (
          <div
            className="glass-effect rounded-lg p-8 animate-fade-in"
            key={selectedIndustry}
          >
            <h3 className="text-2xl font-semibold text-foreground mb-6">
              {selectedIndustryData.name} Use Cases
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedIndustryData.useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-4 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors duration-250"
                >
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <p className="text-muted-foreground leading-relaxed">{useCase}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default IndustrySection;